/*
 * A campaign's link, kept until the booking (DECISIONS D21; mpgadmin G148,
 * D100). A salon's campaign email or text links through
 * mypetgroomer.com.au/r/c/{token}; the API records the click and sends the
 * client here with ?c={token}. The token is kept 7 days (the campaign's
 * window), sent with the booking, and forgotten once a booking is made or at
 * sign-out (G1). It is opaque here: only the API can read it, and a token the
 * API doesn't accept never stops a booking.
 */

export const CAMPAIGN_LINK_KEY = 'mpg_campaign_v1';

/** The campaign's window: a link older than this is forgotten. */
export const CAMPAIGN_LINK_DAYS = 7;

const TOKEN = /^[A-Za-z0-9_-]{22}$/;

interface Kept {
  token: string;
  at: number;
}

const read = (): Kept | null => {
  try {
    const raw = localStorage.getItem(CAMPAIGN_LINK_KEY);
    if (!raw) return null;
    const kept = JSON.parse(raw) as Kept;
    return typeof kept?.token === 'string' && typeof kept?.at === 'number' ? kept : null;
  } catch {
    return null;
  }
};

/**
 * Keeps `?c=` from the address, when it reads as a token, and answers the
 * address without it (null when there was nothing to take out). A newer
 * link replaces an older one.
 */
export const captureCampaignLink = (search: string, now: number = Date.now()): string | null => {
  const params = new URLSearchParams(search);
  const token = params.get('c');
  if (token === null) return null;
  if (TOKEN.test(token)) {
    try {
      localStorage.setItem(CAMPAIGN_LINK_KEY, JSON.stringify({ token, at: now } as Kept));
    } catch {
      /* storage off: the booking just goes without it */
    }
  }
  params.delete('c');
  const rest = params.toString();
  return rest ? `?${rest}` : '';
};

/** The kept token, while it's inside the window; an old one is forgotten here. */
export const campaignToken = (now: number = Date.now()): string | null => {
  const kept = read();
  if (!kept) return null;
  if (now - kept.at > CAMPAIGN_LINK_DAYS * 24 * 60 * 60 * 1000 || !TOKEN.test(kept.token)) {
    forgetCampaignLink();
    return null;
  }
  return kept.token;
};

export const forgetCampaignLink = (): void => {
  try {
    localStorage.removeItem(CAMPAIGN_LINK_KEY);
  } catch {
    /* nothing kept */
  }
};
